import { Injectable } from '@angular/core';
import { ETipoPermiso } from '@core/enums/tipoPermiso.enum';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PermisoService {
  protected permisosDeUsuario = new BehaviorSubject<ETipoPermiso[]>([]);

  /**
   * Mapa de permisos implícitos.
   * Si un usuario tiene el permiso clave, automáticamente tiene acceso a los permisos del valor.
   */
  private readonly permisosImplicitos: Map<ETipoPermiso, ETipoPermiso[]> = new Map([
    // Permisos de reporte implican acceso a listas asociadas
    [
      ETipoPermiso.VENTA_OBTENER_REPORTE,
      [ETipoPermiso.VENTA_VER_LISTA, ETipoPermiso.EMPLEADO_VER_LISTA],
    ],
    [
      ETipoPermiso.MOVIMIENTO_CAJA_OBTENER_REPORTE,
      [ETipoPermiso.MOVIMIENTO_CAJA_VER_LISTA, ETipoPermiso.EMPLEADO_VER_LISTA],
    ],
    [ETipoPermiso.CAJA_TURNO_OBTENER_REPORTE, [ETipoPermiso.EMPLEADO_VER_LISTA]],
    [ETipoPermiso.KARDEX_OBTENER_REPORTE, [ETipoPermiso.PRODUCTO_VER_LISTA]],

    // Permisos de edición implican acceso a vista
    [ETipoPermiso.CLIENTE_EDITAR, [ETipoPermiso.CLIENTE_VER_LISTA]],
    [ETipoPermiso.CLIENTE_ELIMINAR, [ETipoPermiso.CLIENTE_VER_LISTA]],
    [ETipoPermiso.PRODUCTO_EDITAR, [ETipoPermiso.PRODUCTO_VER_LISTA]],
    [ETipoPermiso.PRODUCTO_ELIMINAR, [ETipoPermiso.PRODUCTO_VER_LISTA]],
    [ETipoPermiso.CATEGORIA_EDITAR, [ETipoPermiso.CATEGORIA_VER_LISTA]],
    [ETipoPermiso.CATEGORIA_ELIMINAR, [ETipoPermiso.CATEGORIA_VER_LISTA]],
    [ETipoPermiso.TIPO_PAGO_EDITAR, [ETipoPermiso.TIPO_PAGO_VER_LISTA]],
    [ETipoPermiso.TIPO_PAGO_ELIMINAR, [ETipoPermiso.TIPO_PAGO_VER_LISTA]],
    [ETipoPermiso.MOVIMIENTO_CAJA_EDITAR, [ETipoPermiso.MOVIMIENTO_CAJA_VER_LISTA]],
    [ETipoPermiso.MOVIMIENTO_CAJA_ELIMINAR, [ETipoPermiso.MOVIMIENTO_CAJA_VER_LISTA]],
    [ETipoPermiso.VENTA_EDITAR, [ETipoPermiso.VENTA_VER_LISTA]],
    [ETipoPermiso.VENTA_ELIMINAR, [ETipoPermiso.VENTA_VER_LISTA]],
    [ETipoPermiso.EMPLEADO_EDITAR, [ETipoPermiso.EMPLEADO_VER_LISTA]],
    [ETipoPermiso.EMPLEADO_ELIMINAR, [ETipoPermiso.EMPLEADO_VER_LISTA]],
    [ETipoPermiso.EMPLEADO_VER_LISTA_PERMISOS, [ETipoPermiso.EMPLEADO_VER_LISTA]],
    [ETipoPermiso.GESTOR_TIEMPO_EDITAR, [ETipoPermiso.GESTOR_TIEMPO_VER_LISTA]],
    [ETipoPermiso.GESTOR_TIEMPO_ELIMINAR, [ETipoPermiso.GESTOR_TIEMPO_VER_LISTA]],
    [ETipoPermiso.SUCURSAL_EDITAR, [ETipoPermiso.SUCURSAL_VER_LISTA]],
    [ETipoPermiso.SUCURSAL_ELIMINAR, [ETipoPermiso.SUCURSAL_VER_LISTA]],
    [ETipoPermiso.COMANDAS_EDITAR, [ETipoPermiso.COMANDAS_VER]],
    [ETipoPermiso.COMANDAS_ELIMINAR, [ETipoPermiso.COMANDAS_VER]],
  ]);

  /**
   * Permisos que tiene un VENDEDOR por defecto.
   */
  private readonly permisosVendedor: ETipoPermiso[] = [
    ETipoPermiso.PRODUCTO_VER_LISTA,
    ETipoPermiso.TIPO_PAGO_VER_LISTA,
    ETipoPermiso.CLIENTE_VER_LISTA,
    ETipoPermiso.VENTA_CREAR,
  ];

  get permisos$() {
    return this.permisosDeUsuario.asObservable();
  }

  setPermisos(permisos: ETipoPermiso[]) {
    this.permisosDeUsuario.next(permisos);
  }

  /**
   * Verifica si el usuario tiene el permiso especificado, considerando permisos implícitos.
   * @param permiso El permiso a verificar.
   * @returns Un booleano que indica si el usuario tiene el permiso.
   */
  tienePermiso(permiso: ETipoPermiso): boolean {
    const permisosUsuario = this.permisosDeUsuario.getValue();

    // Los administradores tienen todos los permisos
    if (permisosUsuario.includes(ETipoPermiso.ADMINISTRADOR)) return true;

    // Verificar permisos directos
    if (permisosUsuario.includes(permiso)) return true;

    // Verificar permisos vendedor
    if (
      this.permisosVendedor.includes(permiso) &&
      permisosUsuario.includes(ETipoPermiso.VENDEDOR)
    ) {
      return true;
    }

    // Verificar permisos implícitos
    return this.tienePermisoImplicito(permiso, permisosUsuario);
  }
  /**
   * Verifica si el usuario tiene AL MENOS UNO de los permisos especificados, considerando permisos implícitos.
   * @param permisos Array de permisos a verificar
   * @returns true si tiene al menos uno de los permisos
   */
  tieneAlgunPermiso(permisos: ETipoPermiso[]): boolean {
    return permisos.some((permiso) => this.tienePermiso(permiso));
  }

  /**
   * Verifica si un permiso solicitado está implícitamente concedido por otros permisos del usuario.
   * @param permisoRequerido El permiso requerido.
   * @param permisosUsuario Los permisos que tiene el usuario.
   * @returns true si el permiso está implícitamente concedido, false en caso contrario.
   */
  private tienePermisoImplicito(
    permisoRequerido: ETipoPermiso,
    permisosUsuario: ETipoPermiso[],
  ): boolean {
    // Buscar todos los permisos que el usuario tiene que implican el permiso requerido
    for (const [permisoUsuario, permisosImplicados] of this.permisosImplicitos.entries()) {
      if (
        permisosUsuario.includes(permisoUsuario) &&
        permisosImplicados.includes(permisoRequerido)
      ) {
        return true;
      }
    }
    return false;
  }
}
